/**
 * UX guards for NIFTY Signal Analyzer page (Phase 3).
 * Latest Signal = /latest-signal (never expires in UI).
 * Signal Analysis active card = DB signal within window OR scheduler run with executable payload.
 */

/** Signal Analysis card stays "active" for this long after DB save time. */
export const SIGNAL_ANALYSIS_ACTIVE_MS = 30 * 60 * 1000;

export function parseTimestampMs(value) {
  if (value == null || value === '') return NaN;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? NaN : ms;
}

export function isWithinSignalAnalysisWindow(timestampMs) {
  if (Number.isNaN(timestampMs)) return false;
  return Date.now() - timestampMs < SIGNAL_ANALYSIS_ACTIVE_MS;
}

/** Timestamp for 30m window: prefer /latest-signal response time, then signal.timestamp. */
export function getDbSignalTimestampMs(dbSignal, dbTimestamp) {
  return parseTimestampMs(dbTimestamp ?? dbSignal?.timestamp);
}

export function isRecentDbSavedSignal(dbSignal, dbTimestamp) {
  if (!dbSignal || !dbSignal.signal_id) return false;
  return isWithinSignalAnalysisWindow(getDbSignalTimestampMs(dbSignal, dbTimestamp));
}

/**
 * Scheduler run says no setup, but DB still has a recent saved signal → keep active card.
 */
export function shouldPreserveActiveSignalCard(runStatus, dbSignal, dbTimestamp) {
  if (runStatus !== 'no_active_signal' && runStatus !== 'no_signal') {
    return false;
  }
  return isRecentDbSavedSignal(dbSignal, dbTimestamp);
}

/** Payload from analysis run must not open/refresh executable card. */
export function isNonExecutableSignalPayload(signal) {
  if (!signal || typeof signal !== 'object') return false;
  return Boolean(signal.save_failed || signal.cooldown_blocked);
}

/** Active Signal Analysis card from DB signal + latest scheduler run fields. */
export function buildPreservedActiveResult(runData, dbSignal) {
  return {
    ...runData,
    status: 'active_signal',
    signal: dbSignal,
    message:
      runData?.message ||
      'Showing last saved signal from database (scheduler run had no new active setup).',
  };
}

export function buildCooldownNoticeFromSignal(signal, fallbackTimestamp) {
  return {
    timestamp: signal?.timestamp || fallbackTimestamp || new Date().toISOString(),
    strategy: signal?.strategy_name || signal?.strategy_type || 'Unknown strategy',
    signalType: signal?.signal_type || 'Unknown',
    instrument: signal?.instrument || null,
  };
}

/**
 * Play alert only when signal_id is new after initial hydration (not page load).
 * Returns true if sound was eligible (caller may still debounce inside play fn).
 */
export function shouldPlaySoundForSignalId(signalId, hydrationState) {
  if (!signalId) return false;
  const { hasHydrated, lastKnownId } = hydrationState;
  if (!hasHydrated) return false;
  return lastKnownId !== signalId;
}

/** Update hydration refs after /latest-signal fetch; returns whether this was first hydrate. */
export function hydrateLatestSignalIdRefs(signalId, refs) {
  const firstHydrate = !refs.hasHydrated.current;
  if (firstHydrate) {
    refs.hasHydrated.current = true;
    refs.lastKnownId.current = signalId || null;
    return { firstHydrate: true, isNew: false };
  }
  const isNew = Boolean(signalId && refs.lastKnownId.current !== signalId);
  if (isNew) {
    refs.lastKnownId.current = signalId;
  }
  return { firstHydrate: false, isNew };
}
